import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
  Stack,
  Chip
} from '@mui/material';
import { Search as SearchIcon, Security as SecurityIcon, Download as DownloadIcon, ArrowDropUp, ArrowDropDown } from '@mui/icons-material';
import dashboardApiService from '../../services/dashboard/DashboardApiService';
import dockerApiService from '../../services/alerts/DockerApiService';

// Column layout (filtered to fields present in API per request)
const columnDefs = [
  { key: 'timestamp', label: 'timestamp', paths: ['@timestamp', 'timestamp'] },
  { key: 'rule_description', label: 'rule.description', paths: ['rule.description', 'description'] },
  { key: 'rule_id', label: 'rule.id', paths: ['rule.id', 'rule_id', 'id'] },
  { key: 'count', label: 'Count', paths: ['count', 'doc_count'], fallback: 1 },
];

const DockerInfo = () => {
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({ page: 1, size: 25, total: 0 });
  const [sort, setSort] = useState({ key: 'timestamp', dir: 'desc' });

  useEffect(() => {
    const loadAgents = async () => {
      try {
        const list = await dashboardApiService.getAllAgents();
        setAgents(list || []);
        if (list && list.length > 0) setSelectedAgent(list[0].id);
      } catch (e) {
        console.error('Failed to load agents', e);
      }
    };
    loadAgents();
  }, []);

  const normalizeResponse = (res) => {
    if (!res) return [];
    if (Array.isArray(res)) return res;
    if (Array.isArray(res?.alerts)) return res.alerts;
    if (Array.isArray(res?.containers)) return res.containers;
    if (Array.isArray(res?.data)) return res.data;
    // Single object fallback
    return [res];
  };

  const getVal = (obj, path) => {
    try {
      return path.split('.').reduce((acc, part) => {
        if (acc == null) return undefined;
        // support bracket-like keys with dots inside? keep simple
        return acc[part];
      }, obj);
    } catch (_) {
      return undefined;
    }
  };

  const getFirstAvailable = (obj, paths, fallback) => {
    for (const p of paths) {
      const v = getVal(obj, p.replace('?.', '.'));
      if (v !== undefined && v !== null && v !== '') return v;
    }
    return fallback;
  };

  const toDisplayRow = (item) => {
    const out = {};
    columnDefs.forEach((c) => {
      out[c.key] = getFirstAvailable(item, c.paths, c.fallback);
    });
    return out;
  };

  const formatHHmm = (v) => {
    if (!v) return '';
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return String(v);
    return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false });
  };

  const handleFetch = async () => {
    if (!selectedAgent) return;
    try {
      setLoading(true);
      setError(null);
      setRows([]);
      const res = await dockerApiService.getDockerInfoByAgent(selectedAgent);
      const list = normalizeResponse(res).map(toDisplayRow);
      setRows(list);
      setPagination((p) => ({ ...p, page: 1, total: list.length }));
    } catch (e) {
      console.error('Docker info fetch failed', e);
      setError('Failed to fetch Docker info');
    } finally {
      setLoading(false);
    }
  };

  const allColumns = useMemo(() => columnDefs.map((c) => c.key), []);

  const filteredRows = useMemo(() => {
    if (!searchTerm) return rows;
    const term = searchTerm.toLowerCase();
    return rows.filter((r) => allColumns.some((c) => String(r[c] ?? '').toLowerCase().includes(term)));
  }, [rows, searchTerm, allColumns]);

  const sortedRows = useMemo(() => {
    const copy = [...filteredRows];
    const { key, dir } = sort;
    copy.sort((a, b) => {
      const va = a[key];
      const vb = b[key];
      if (key === 'timestamp') {
        const da = new Date(va).getTime();
        const db = new Date(vb).getTime();
        return (da - db) * (dir === 'asc' ? 1 : -1);
      }
      const sa = String(va ?? '').toLowerCase();
      const sb = String(vb ?? '').toLowerCase();
      if (sa < sb) return dir === 'asc' ? -1 : 1;
      if (sa > sb) return dir === 'asc' ? 1 : -1;
      return 0;
    });
    return copy;
  }, [filteredRows, sort]);

  const pageRows = useMemo(() => {
    const start = (pagination.page - 1) * pagination.size;
    return sortedRows.slice(start, start + pagination.size);
  }, [sortedRows, pagination]);

  useEffect(() => {
    setPagination((p) => ({ ...p, page: 1, total: filteredRows.length }));
  }, [filteredRows]);

  const handlePageChange = (e, newPage) => setPagination((p) => ({ ...p, page: newPage }));
  const onSort = (colKey) => {
    setSort((s) => ({ key: colKey, dir: s.key === colKey && s.dir === 'asc' ? 'desc' : 'asc' }));
  };

  const exportCsv = () => {
    const headers = columnDefs.map((c) => c.label);
    const rowsData = sortedRows.map((r) =>
      columnDefs.map((c) => {
        const v = r[c.key];
        const cell = c.key === 'timestamp' ? formatHHmm(v) : v;
        const s = String(cell ?? '');
        if (s.includes(',') || s.includes('"') || s.includes('\n')) {
          return '"' + s.replace(/"/g, '""') + '"';
        }
        return s;
      }).join(',')
    );
    const csv = [headers.join(','), ...rowsData].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'docker-info.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 1, fontWeight: 600 }}>
        Docker info
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Select an agent and fetch Docker information for that agent.
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Select Agent</InputLabel>
            <Select
              label="Select Agent"
              value={selectedAgent}
              onChange={(e) => setSelectedAgent(e.target.value)}
            >
              {agents.map((a) => (
                <MenuItem key={a.id} value={a.id}>
                  {a.name} ({a.ip})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            size="small"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />

          <Button variant="contained" onClick={handleFetch} disabled={!selectedAgent || loading} startIcon={<SecurityIcon />}>
            Docker info
          </Button>

          {loading && <CircularProgress size={22} />}

          <Chip label={`Total: ${filteredRows.length}`} size="small" />
          <Button variant="text" size="small" startIcon={<DownloadIcon />} onClick={exportCsv}>
            Export
          </Button>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              {columnDefs.map((c) => {
                const active = sort.key === c.key;
                return (
                  <TableCell key={c.key} sx={{ fontWeight: 600, whiteSpace: 'nowrap', cursor: 'pointer' }} onClick={() => onSort(c.key)}>
                    {c.label}
                    {active && (sort.dir === 'asc' ? <ArrowDropUp fontSize="small" /> : <ArrowDropDown fontSize="small" />)}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={allColumns.length} align="center">
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress size={24} />
                  </Box>
                </TableCell>
              </TableRow>
            ) : pageRows.length > 0 ? (
              pageRows.map((row, idx) => (
                <TableRow key={idx} hover>
                  {columnDefs.map((c) => (
                    <TableCell key={c.key} sx={{ maxWidth: 360, whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                      {c.key === 'timestamp' ? formatHHmm(row[c.key]) : String(row[c.key] ?? '')}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={allColumns.length} align="center">
                  No data. Select an agent and click Docker info.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {filteredRows.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
            <Typography variant="body2">
              Showing {(pagination.page - 1) * pagination.size + 1} to {Math.min(pagination.page * pagination.size, filteredRows.length)} of {filteredRows.length}
            </Typography>
            <Pagination
              count={Math.ceil(filteredRows.length / pagination.size)}
              page={pagination.page}
              onChange={handlePageChange}
              color="primary"
              size="small"
              showFirstButton
              showLastButton
            />
          </Stack>
        </Box>
      )}
    </Box>
  );
};

export default DockerInfo;
